import {ArrowLeft, Eye, EyeOff} from 'lucide-react';
import {useState, type FormEvent} from 'react';
import {Navigate, useNavigate} from 'react-router-dom';
import {postLogin} from '@/api/auth';
import Logo from '@/components/logo';
import {Button, Input} from '@/components/ui';
import {setTokens} from '@/lib/token-storage';
import {cn} from '@/lib/utils';
import {canUseConsole, useAuthStore} from '@/stores/auth';

enum Step {
  EMAIL,
  PASSWORD
}

const EMAIL_PATTERN = /[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,64}/;

/**
 * The app's two-step login (app/(auth)/login.tsx): email, then password, on
 * POST /auth/login. Controller accounts (role `fir_atc`) get the ATC home,
 * pilots (role `pilot`) the Flight Agent (app: screens/pilot-atc).
 */
export default function SignInPage() {
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const setUser = useAuthStore((s) => s.setUser);
  const [step, setStep] = useState(Step.EMAIL);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  if (status === 'authenticated') return <Navigate to="/" replace />;

  const emailError =
    email && (email.length > 250 || !EMAIL_PATTERN.test(email))
      ? 'Please enter a valid email address'
      : null;
  const passwordError =
    password && password.length < 8 ? 'Password should have at least 8 characters' : null;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (step === Step.EMAIL) {
      if (!email || emailError) return;
      setError(null);
      setStep(Step.PASSWORD);
      return;
    }
    if (!password || passwordError || processing) return;
    setError(null);
    setProcessing(true);
    const res = await postLogin(email.trim(), password);
    setProcessing(false);
    if (res?.error || !res?.data?.access_token) {
      setError(res?.error_message || res?.message || 'Cannot connect to server! Please try again.');
      return;
    }
    const {access_token, refresh_token, user} = res.data;
    if (!canUseConsole(user)) {
      setError('This console is for ATC and pilot accounts only.');
      return;
    }
    setTokens(access_token, refresh_token);
    setUser(user);
    navigate('/', {replace: true});
  };

  const onBack = () => {
    setError(null);
    setPassword('');
    setStep(Step.EMAIL);
  };

  const fieldError = step === Step.EMAIL ? emailError : passwordError;
  const disabled =
    processing || (step === Step.EMAIL ? !email || !!emailError : !password || !!passwordError);

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-between bg-[#000F0A] px-4">
      <div className="mt-[10vh] w-full">
        <form
          onSubmit={onSubmit}
          className="mx-auto mt-6 w-full max-w-[424px] rounded-[24px] bg-[#061510] p-8"
        >
          <div className="flex flex-col items-center justify-center">
            <Logo color="#D9F99D" />
            <h1 className="mt-6 text-center text-2xl font-medium leading-8 tracking-[-0.48px] text-[#E7E5E4]">
              Log in to Accumulus AI
            </h1>
          </div>

          {step === Step.EMAIL ? (
            <div className="mt-8">
              <label
                htmlFor="email"
                className="mb-1.5 block font-mono text-base uppercase leading-6 tracking-[-0.32px] text-[#D6D3D1]"
              >
                Email address
              </label>
              <Input
                id="email"
                type="email"
                autoFocus
                autoComplete="username"
                placeholder="Required"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={cn(
                  'h-12 border-[#1F2D27] bg-[#0C1A16] text-base text-[#E7E5E4] placeholder:text-[#57534E]',
                  {'border-[#C71921]': !!emailError}
                )}
              />
            </div>
          ) : (
            <div className="mt-8">
              <div className="mb-1.5 flex items-center justify-between">
                <label htmlFor="password" className="text-base leading-6 text-[#CECFD2]">
                  Password
                </label>
                <span className="truncate pl-4 text-sm leading-5 text-[#A8A29E]">{email}</span>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoFocus
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={cn(
                    'h-12 border-[#1F2D27] bg-[#0C1A16] pr-12 text-base text-[#E7E5E4] placeholder:text-[#57534E]',
                    {'border-[#C71921]': !!passwordError}
                  )}
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-[#E7E5E4]"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
          )}

          {(fieldError || error) && (
            <p className="mt-1.5 text-sm leading-5 text-[#F87171]">{error ?? fieldError}</p>
          )}

          <Button
            type="submit"
            disabled={disabled}
            isLoading={processing}
            className="mt-4 h-10 w-full rounded-[10px] text-sm font-semibold"
          >
            {step === Step.EMAIL ? 'Continue' : 'Log In'}
          </Button>

          {step === Step.PASSWORD && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={onBack}
                className="flex items-center gap-0.5 text-sm text-[#38BDF8]"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            </div>
          )}
        </form>
      </div>
      <p className="mb-6 mt-8 text-center text-xs leading-4 text-[#57534E]">
        © {new Date().getFullYear()} Accumulus AI. All rights reserved.
      </p>
    </div>
  );
}
